'use client'

import { useState, useEffect, useRef, Suspense } from 'react'
import Link from 'next/link'
import { useSearchParams, useRouter } from 'next/navigation'
import { Logo } from '~/app/_components/Logo'
import { RegistrationStepper } from '~/app/_components/RegistrationStepper'
import { AuthProviderStep } from '~/app/_features/join/AuthProviderStep'
import { BasicInfoStep } from '~/app/_features/join/BasicInfoStep'
import { api } from '~/trpc/react'
import { auth } from '~/components/FirebaseProvider'
import { onAuthStateChanged, updateProfile } from 'firebase/auth'
import { Avatar } from '~/components/DefaultAvatar'

// Helper function to extract names from displayName
function extractUserNames(displayName: string | null, email: string) {
  let firstName = ''
  let lastName = ''
  
  if (displayName?.trim()) {
    const nameParts = displayName.trim().split(' ')
    firstName = nameParts[0] ?? ''
    lastName = nameParts.slice(1).join(' ') ?? ''
  }
  
  // Fallback: use email username if no displayName
  if (!firstName && email) {
    firstName = email.split('@')[0] ?? ''
  }
  
  return { firstName, lastName }
}

function JoinPageContent() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const [currentStep, setCurrentStep] = useState(1)
  const [firebaseUser, setFirebaseUser] = useState<{
    uid: string
    email: string | null
    displayName: string | null
    photoURL: string | null
  } | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [referredByUserId, setReferredByUserId] = useState<string | null>(null)
  const registerMutation = api.post.registerEarlyUser.useMutation()
  const utils = api.useUtils()
  const hasInitialized = useRef(false)

  // Capture referral ID from URL query parameter on page load
  useEffect(() => {
    const refParam = searchParams?.get('ref')
    if (refParam) {
      // Store in state and localStorage for persistence across auth steps
      setReferredByUserId(refParam)
      try {
        localStorage.setItem('referralUserId', refParam)
      } catch {
        // localStorage might not be available, continue without it
      }
    } else {
      // Check localStorage for persisted referral (in case user navigated away and came back)
      try {
        const storedRef = localStorage.getItem('referralUserId')
        if (storedRef) {
          setReferredByUserId(storedRef)
        }
      } catch {
        // localStorage might not be available
      }
    }
  }, [searchParams])

  // Check if user is already authenticated
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setFirebaseUser({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL ?? null,
        })
      }
    })

    return () => unsubscribe()
  }, [])

  const handleProviderSelected = (
    provider: 'google' | 'email',
    user: { uid: string; email: string | null; displayName: string | null; photoURL: string | null }
  ) => {
    setFirebaseUser(user)
    setCurrentStep(2)
  }

  const handleBasicInfoComplete = async (data: {
    role: 'CAREGIVER' | 'CLIENT'
    firstName: string
    lastName: string
    email: string
    phoneNumber: string
    address: string
    zipCode: string
    lat: number
    long: number
    serviceIds: string[]
    workingRadius?: number | null
  }) => {
    if (!firebaseUser) return

    setErrorMessage(null) // Clear any previous errors

    try {
      // Update Firebase displayName with first + last name
      const currentUser = auth.currentUser
      if (currentUser && data.firstName && data.lastName) {
        const fullName = `${data.firstName} ${data.lastName}`.trim()
        if (fullName) {
          try {
            await updateProfile(currentUser, { displayName: fullName })
          } catch (profileError) {
            // Non-critical error, log but continue
            console.warn('Failed to update Firebase displayName:', profileError)
          }
        }
      }

      await registerMutation.mutateAsync({
        firebaseUid: firebaseUser.uid,
        role: data.role,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        address: data.address,
        zipCode: data.zipCode,
        lat: data.lat,
        long: data.long,
        serviceIds: data.serviceIds,
        referredByUserId: referredByUserId ?? undefined,
      })

      // Clear referral from localStorage after successful registration
      try {
        localStorage.removeItem('referralUserId')
      } catch {
        // Ignore localStorage errors
      }

      // Refetch earlyRegistration query to ensure it's up to date
      await utils.post.getEarlyRegistrationByFirebaseUid.invalidate({ firebaseUid: firebaseUser.uid })
      
      router.push('/app/profile')
    } catch (error) {
      console.error('Error registering user:', error)
      
      // Extract error message from tRPC error
      let message = 'Failed to complete registration. Please try again.'
      if (error && typeof error === 'object' && 'message' in error) {
        message = String(error.message)
      } else if (error instanceof Error) {
        message = error.message
      }
      
      setErrorMessage(message)
      // Still advance to step 3 if EarlyRegistration was created (partial success)
      // The mutation should return success even if profile creation fails
      if (registerMutation.data?.earlyRegistrationCreated) {
        await utils.post.getEarlyRegistrationByFirebaseUid.invalidate({ firebaseUid: firebaseUser.uid })
        router.push('/app/profile')
      }
    }
  }

  // Check if user exists (with auto-creation)
  const { data: existingUser } = api.post.getUserByFirebaseUid.useQuery(
    { 
      firebaseUid: firebaseUser?.uid ?? '',
      email: firebaseUser?.email ?? undefined,
      displayName: firebaseUser?.displayName ?? undefined,
    },
    { enabled: !!firebaseUser?.uid }
  )

  // Check if EarlyRegistration exists (indicates completed flow)
  const { data: earlyRegistration } = api.post.getEarlyRegistrationByFirebaseUid.useQuery(
    { firebaseUid: firebaseUser?.uid ?? '' },
    { enabled: !!firebaseUser?.uid && !!existingUser }
  )

  // Fetch referrer user data if we have a referral ID
  const { data: referrerUser } = api.post.getUserById.useQuery(
    { id: referredByUserId ?? '' },
    { enabled: !!referredByUserId }
  )

  // Determine initial step based on EarlyRegistration existence (only on first load)
  useEffect(() => {
    // Only run this logic once on initial load
    if (hasInitialized.current) {
      return
    }

    if (!firebaseUser) {
      // No auth yet → stay at step 1
      hasInitialized.current = true
      return
    }
    
    // Wait for queries to finish loading
    if (existingUser === undefined || earlyRegistration === undefined) {
      return
    }
    
    // Now we have all the data, set initial step
    if (earlyRegistration) {
      router.push('/app/profile')
      return
    }
    // Otherwise stay at step 1 (user needs to go through flow)
    
    hasInitialized.current = true
  }, [firebaseUser, existingUser, earlyRegistration])

  const getInitialData = () => {
    if (!firebaseUser) return undefined

    // If we have EarlyRegistration, use that data for editing
    if (earlyRegistration?.registrationData) {
      const data = earlyRegistration.registrationData as {
        firstName?: string
        lastName?: string
        email?: string
        phoneNumber?: string
        address?: string
        zipCode?: string
        serviceIds?: string[]
      }
      return {
        firstName: data.firstName ?? '',
        lastName: data.lastName ?? '',
        email: data.email ?? firebaseUser.email ?? '',
        phoneNumber: data.phoneNumber ?? '',
        address: data.address ?? '',
        zipCode: data.zipCode ?? '',
        serviceIds: data.serviceIds ?? [],
      }
    }

    // Fallback to Firebase displayName/email
    const { firstName, lastName } = extractUserNames(
      firebaseUser.displayName,
      firebaseUser.email ?? ''
    )

    return {
      firstName,
      lastName,
      email: firebaseUser.email ?? '',
    }
  }

  return (
    <div className="bg-gray-50 min-h-screen relative">
      {/* Referrer Pill - Bottom Right Corner */}
      {referrerUser && (
        <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 max-w-[calc(100vw-2rem)]">
          <div className="flex items-center gap-2 sm:gap-3 bg-white rounded-full shadow-lg border border-gray-200 px-3 py-2 sm:px-4 sm:py-2.5 hover:shadow-xl transition-all duration-200">
            <div className="text-xs font-medium text-gray-500 whitespace-nowrap hidden sm:block">Referred by</div>
            <div className="flex items-center gap-2 sm:gap-2.5">
              <div className="relative w-8 h-8 sm:w-9 sm:h-9 rounded-full shrink-0 ring-2 ring-white">
                <Avatar
                  src={
                    referrerUser.Media?.find(m => m.type === 'IMAGE')?.url ??
                    referrerUser.Caregiver?.Media?.find(m => m.type === 'IMAGE')?.url ??
                    null
                  }
                  alt={`${referrerUser.firstName} ${referrerUser.lastName}`}
                  size={36}
                  className="w-full h-full"
                />
              </div>
              <div className="text-xs sm:text-sm font-semibold text-gray-900 whitespace-nowrap truncate max-w-[120px] sm:max-w-none">
                {referrerUser.firstName} {referrerUser.lastName}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-8">
            <Link href="/" aria-label="Home" className="inline-block">
              <Logo className="h-16 w-auto mx-auto" />
            </Link>
          </div>

          <div className="bg-white rounded-lg shadow-xl p-8 sm:p-10">
            <RegistrationStepper 
              currentStep={currentStep} 
              totalSteps={2}
              onStepClick={setCurrentStep}
              enabledSteps={[
                1,
                ...(currentStep >= 2 || firebaseUser ? [2] : []),
              ]}
            />

            {currentStep === 1 && (
              <AuthProviderStep onProviderSelected={handleProviderSelected} />
            )}

                {currentStep === 2 && firebaseUser && (
                  <BasicInfoStep
                    firebaseUid={firebaseUser.uid}
                    authPhotoURL={firebaseUser.photoURL}
                    initialData={getInitialData()}
                    onComplete={handleBasicInfoComplete}
                  />
                )}

                {errorMessage && (
                  <div className="mt-4 p-4 border rounded-md bg-red-50 border-red-200">
                    <div className="flex">
                      <div className="shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">
                          Registration Error
                        </h3>
                        <p className="mt-1 text-sm text-red-700">
                          {errorMessage}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
          </div>
        </div>
      </div>
    </div>
  )
}


export default function JoinPage() {
  return (
    <Suspense fallback={
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    }>
      <JoinPageContent />
    </Suspense>
  )
}
